/**
 * SDK client singletons + trade-symbol helpers.
 *
 * Three clients, each with a distinct role:
 *   - `InfoClient`          — REST reads. Stateless, safe to share.
 *   - `SubscriptionClient`  — WebSocket push. Single connection, multiplexed
 *                             across subscriptions (orderbook, trades, user
 *                             events). Kept on `globalThis` so HMR doesn't
 *                             leak connections.
 *   - `ExchangeClient`      — signed writes. Lazily created on first wallet
 *                             connect (`initExchangeClient`), reset on wallet
 *                             change (`resetExchangeClient`).
 *
 * XYZ DEX coins travel with a `xyz:` prefix in API responses; use
 * `stripDexPrefix` on the way in and `getApiCoin` on the way out.
 */
import {
	ExchangeClient,
	HttpTransport,
	InfoClient,
	SubscriptionClient,
	WebSocketTransport,
} from "@nktkas/hyperliquid";
import type { AbstractWallet } from "@nktkas/hyperliquid/signing";
import { getXyzInstrument, getXyzMarketName } from "./xyz-markets";

export { getXyzInstrument, getXyzMarketName } from "./xyz-markets";

export type TradeMarketIdentity = {
	routeSymbol: string;
	coin: string;
	chartSymbol: string;
	displaySymbol: string;
	displayName: string;
	dex?: string;
};

export const XYZ_DEX_NAME = "xyz";

export const KNOWN_MARKET_NAMES: Record<string, string> = {
	BTC: "Bitcoin",
	ETH: "Ethereum",
	SOL: "Solana",
	XRP: "Ripple",
	DOGE: "Dogecoin",
	SUI: "Sui",
	HYPE: "Hyperliquid",
};

export function getTradeMarketName(coin: string, dex?: string) {
	if (dex === XYZ_DEX_NAME) return getXyzMarketName(coin);
	return KNOWN_MARKET_NAMES[coin] ?? coin;
}

export function stripDexPrefix(name: string): string {
	const colonIndex = name.indexOf(":");
	return colonIndex !== -1 ? name.slice(colonIndex + 1) : name;
}

// Maps display coin name → API-facing coin name (e.g. "SMSN" → "xyz:SMSN")
const apiCoinRegistry = new Map<string, string>();

export function registerApiCoin(displayCoin: string, apiCoin: string) {
	apiCoinRegistry.set(displayCoin, apiCoin);
}

export function getApiCoin(coin: string): string {
	return apiCoinRegistry.get(coin) ?? coin;
}

export function createTradeRouteSymbol(coin: string, dex?: string) {
	const upper = coin.toUpperCase().trim();
	// URL-safe slug uses a hyphen, NOT a colon. A `:` in a path segment breaks
	// sitemap URLs (scheme ambiguity) and static serving. The internal HL/chart
	// symbol keeps the colon (see `chartSymbol`).
	if (dex === XYZ_DEX_NAME) return `${XYZ_DEX_NAME}-${upper}`;
	return upper;
}

const SUPPORTED_RESOLUTIONS = [
	"1",
	"3",
	"5",
	"15",
	"30",
	"60",
	"120",
	"240",
	"480",
	"720",
	"1D",
	"3D",
	"1W",
	"1M",
] as const;

// Testnet toggle for E2E runs (`bun run e2e:testnet`). Flips the REST/WS
// endpoints AND the L1 signature domain (source "a"→"b") inside the SDK, so it
// must agree with the backend's HYPERLIQUID_MAINNET flag or every signed
// action is rejected by the venue.
export const HL_TESTNET = import.meta.env?.VITE_HL_TESTNET === "true";

const httpTransport = new HttpTransport({ isTestnet: HL_TESTNET });
const infoClient = new InfoClient({ transport: httpTransport });

const WS_KEY = "__hl_subscription_client" as const;

type HlGlobal = typeof globalThis & {
	[WS_KEY]?: { client: SubscriptionClient; transport: WebSocketTransport };
};

export function getInfoClient() {
	return infoClient;
}

export function getSubscriptionClient() {
	if (typeof window === "undefined") return null;

	const g = globalThis as HlGlobal;
	if (g[WS_KEY]) return g[WS_KEY].client;

	// rews v4 already defaults maxRetries to Infinity, but keep it explicit —
	// a finite value (v3 defaulted to 3, exhausted in ~1s on sleep/wake)
	// permanently and silently terminates the transport, freezing every WS
	// subscription until full page reload.
	const transport = new WebSocketTransport({
		isTestnet: HL_TESTNET,
		reconnect: { maxRetries: Number.POSITIVE_INFINITY },
	});
	const client = new SubscriptionClient({ transport });
	g[WS_KEY] = { client, transport };
	return client;
}

let exchangeClient: ExchangeClient | null = null;

export function getExchangeClient() {
	return exchangeClient;
}

export function initExchangeClient(wallet: AbstractWallet) {
	if (!exchangeClient) {
		exchangeClient = new ExchangeClient({
			transport: httpTransport,
			wallet,
		});
	}
	return exchangeClient;
}

export function resetExchangeClient() {
	exchangeClient = null;
}

export function normalizeTradeRouteSymbol(rawSymbol: string) {
	const symbol = rawSymbol.toUpperCase().trim();

	const directMatch = symbol.match(/^([A-Z0-9]+)[-_:/](?:PERP|USD|USDT)$/);
	if (directMatch) return directMatch[1];

	if (symbol.endsWith("USDT")) return symbol.slice(0, -4);
	if (symbol.endsWith("USD")) return symbol.slice(0, -3);
	if (symbol.endsWith("PERP")) return symbol.slice(0, -4).replace(/[-_:]$/, "");

	const [firstToken] = symbol.split(/[-_]/);
	return firstToken || "BTC";
}

function parseRouteSymbol(rawSymbol: string): { coin: string; dex?: string } {
	const trimmed = rawSymbol.trim();
	// Accept the URL-safe `xyz-COIN` slug and the legacy/internal `xyz:COIN` form.
	const xyzMatch = trimmed.match(/^xyz[-:](.+)$/i);
	if (xyzMatch) {
		return { coin: xyzMatch[1].toUpperCase(), dex: XYZ_DEX_NAME };
	}
	return { coin: normalizeTradeRouteSymbol(rawSymbol) };
}

export function createTradeMarketIdentity(
	routeSymbol: string,
): TradeMarketIdentity {
	const { coin, dex } = parseRouteSymbol(routeSymbol);
	const prettyName = getTradeMarketName(coin, dex);
	const isXyz = dex === XYZ_DEX_NAME;

	return {
		routeSymbol: createTradeRouteSymbol(coin, dex),
		coin,
		chartSymbol: isXyz ? `${XYZ_DEX_NAME}:${coin}` : coin,
		displaySymbol: isXyz ? `${getXyzInstrument(coin)}-PERP` : `${coin}-PERP`,
		displayName: `${prettyName} Perpetual Futures`,
		dex,
	};
}

export function getSupportedChartResolutions() {
	return [
		...SUPPORTED_RESOLUTIONS,
	] as import("@public/charting_library").ResolutionString[];
}

export function resolutionToInterval(resolution: string) {
	switch (resolution) {
		case "1":
			return "1m";
		case "3":
			return "3m";
		case "5":
			return "5m";
		case "15":
			return "15m";
		case "30":
			return "30m";
		case "60":
			return "1h";
		case "120":
			return "2h";
		case "240":
			return "4h";
		case "480":
			return "8h";
		case "720":
			return "12h";
		case "1D":
			return "1d";
		case "3D":
			return "3d";
		case "1W":
			return "1w";
		case "1M":
			return "1M";
		default:
			return "1h";
	}
}
